import {Component, Input, OnInit} from '@angular/core';
import {
  AdditionalSolution
} from '@app/checkout/additional-required-solutions-button/additional-required-solutions-modal/additional-required-solutions-modal.component';
import {CommercialModelConfig} from '@app/model/dcp-config-service/commercial-model-config';
import {DcpConfigService} from '@app/services/dcp-config.service';
import {TransactionTypeService} from '@app/services/transactionTypes/transaction-type.service';
import {ArrayService, DcpEntryGroup, DcpPriceData, TransactionType} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Product} from '@spartacus/core';
import {Observable, combineLatest, of} from 'rxjs';
import {first, map, mergeMap} from 'rxjs/operators';
import {DcpProductService} from '../../../services/dcp-product.service';
import {DerivativePriceService} from '../../../services/derivative-price.service';
import {EntryGroupService} from '../../../services/entry-group.service';
import {TransactionTypeTexts} from '@app/checkout/order-confirmation/order-confirmation-list-item/order-confirmation-order-item.model';

@UntilDestroy()
@Component({
  selector: 'dcp-price-display',
  templateUrl: './price-display.component.html',
  styleUrls: ['./price-display.component.scss']
})
export class PriceDisplayComponent implements OnInit {

  @Input() renderedComponents: any[] = [];
  @Input() product: Product;
  @Input() isModalOpened: boolean;
  increment = false;
  totalPrice: string;
  isTotalPriceZero: boolean;
  subtotalPrice: string;
  derivativePrice: string;
  supplierAlternativeIdCount: number;
  orderEntriesOneParent: AdditionalSolution[] = [];
  ttTranslations$: Observable<TransactionTypeTexts>;
  commercialModelConfig: CommercialModelConfig;
  isLoading = true;

  private transactionTypeInternal: TransactionType;
  private priceDataInternal: DcpPriceData;

  constructor(
    public entryGroupService: EntryGroupService,
    public derivativePriceService: DerivativePriceService,
    public transactionTypeService: TransactionTypeService,
    private dcpProductService: DcpProductService,
    private dcpConfigService: DcpConfigService
  ) {
  }

  get transactionType(): TransactionType {
    return this.transactionTypeInternal;
  }

  @Input() set transactionType(value: TransactionType) {
    if (value) {
      this.transactionTypeInternal = value;
      this.ttTranslations$ = this.transactionTypeService.getTranslations(value.code);
    }
  }

  get priceData(): DcpPriceData {
    return this.priceDataInternal;
  }

  @Input() set priceData(value: DcpPriceData) {
    if (value) {
      this.isLoading = false;
    }
    if (!value) {
      this.isLoading = true;
    }
    this.priceDataInternal = value;
    this.setTotalValue(this.supplierAlternativeIdCount, this.priceData);
  }

  ngOnInit() {
    this.initCommercialModel();
    this.setRequiredSolutionModalData().pipe(untilDestroyed(this)).subscribe(supportingSolution => {
      this.orderEntriesOneParent = [supportingSolution];
      this.supplierAlternativeIdCount = this.countSupplierAlternativeIds();
      this.setTotalValue(this.supplierAlternativeIdCount, this.priceData);
    });
  }

  /**
   * Counts the number of derivatives that are linked to the product,
   * that is currently displayed in the configurator.
   * They can be provided via the cart or a URL parameter.
   */
  countSupplierAlternativeIds(): number {
    let a: string[] = [];
    for (const orderEntry of this.orderEntriesOneParent) {
      if (orderEntry?.products.some(p => p.product?.supplierAlternativeAID === this.product?.supplierAlternativeAID)) {
        a = a.concat(orderEntry.derivatives.map(d => d.product.supplierAlternativeAID));
      }
    }
    return ArrayService.removeDuplicates(a).length;
  }

  /**
   * As only the supplierAlternativeIds are passed to the frontend a DcpEntryGroup structure must be created.
   * This method adds derivatives to a root group.
   *
   * @param additionalSolution additionalSolution to add the derivatives to.
   * @param derivativeIds supplierAlternativeIds of the derivatives.
   */
  addDerivativesFromApi(additionalSolution: AdditionalSolution, derivativeIds: string[]): Observable<AdditionalSolution> {
    const resultObservables = [];
    for (const supplierAlternativeId of derivativeIds) {
      resultObservables.push(
        this.dcpProductService.fetchProductBySupplierAlternativeId(supplierAlternativeId)
      );
    }
    return combineLatest<DcpEntryGroup[]>(resultObservables).pipe(map(egs => {
      for (const product of egs) {
        additionalSolution.derivatives.push({product} as any);
      }
      return additionalSolution;
    }));
  }

  initCommercialModel() {
    this.dcpConfigService.getCommercialModelConfig(this.product, this.transactionType?.code)
      .pipe(first(), untilDestroyed(this))
      .subscribe((commercialModelConfig: CommercialModelConfig) => {
        this.commercialModelConfig = commercialModelConfig;
      });
  }

  private setTotalValue(derivativeCount: number, priceData: DcpPriceData) {
    this.isTotalPriceZero = Number(this.priceData?.price?.totalValue) === 0;
    if (derivativeCount > 0) {
      this.totalPrice = this.derivativePriceService.getTotalPrice(priceData);
      this.derivativePrice = this.derivativePriceService.getDerivativePrice(priceData);
      this.subtotalPrice = this.priceData?.price?.formattedValues?.totalValue;
    } else {
      this.totalPrice = this.priceData?.price?.formattedValues?.totalValue;
    }
  }

  /**
   * Sets the data that will be displayed in the RequiredSolutionModal.
   * It can either get the data from the cart or collect the products by the supplierAlternativeIds.
   * The supplierAlternativeIds need present as a URL parameter.
   */
  private setRequiredSolutionModalData(): Observable<AdditionalSolution> {
    return this.entryGroupService.getRootGroupsFromActiveCart(this.product.supplierAlternativeAID)
      .pipe(first(),
        mergeMap(entryGroups => {
          const additionalSolution: AdditionalSolution = {
            products: [{product: this.product}],
            derivatives: this.entryGroupService.getOrderEntriesByBundleType(entryGroups, this.entryGroupService.isDerivative)
          };
          if (additionalSolution.derivatives?.length) {
            return of(additionalSolution);
          } else {
            const dIds: string[] = this.entryGroupService.getDerivativeIdsFromUrl();
            return this.addDerivativesFromApi(additionalSolution, dIds);
          }
        }),
        untilDestroyed(this)
      );
  }
}
